import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  Alert,
  Badge,
  Button,
  Card,
  Collapse,
  DatePicker,
  Modal,
  Select,
  Skeleton,
  Table,
  Tabs,
  Tag,
  Tooltip,
} from "antd";
import {
  BankOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ReloadOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { get } from "service/api";
import moneyFormat from "functions/utils/moneyFormat";
import {
  adminInvoiceTxSearchPath,
  invoiceTxDayFromSettleDate,
} from "functions/admin/adminInvoiceDeepLink";

const fmt = (v) => (v !== undefined && v !== null ? `${moneyFormat(v)}₮` : "0₮");

/** POS SETTLEMENT тулгалт зөвхөн эдгээр Khan payment/purchase дансууд дээр */
const POS_RECONCILIATION_ACCOUNTS = new Set(["5570025382", "5570137868"]);

function isPosReconciliationAccount(accountNumber) {
  return POS_RECONCILIATION_ACCOUNTS.has(String(accountNumber || ""));
}

function sumAbsAmount(rows, key = "amount") {
  return (rows || []).reduce(
    (s, r) => s + Math.abs(parseFloat(r[key] ?? 0) || 0),
    0
  );
}

function sumPosAmount(rows) {
  return (rows || []).reduce((s, r) => {
    if (r.posAmount == null || r.posAmount === "") return s;
    return s + (parseFloat(r.posAmount) || 0);
  }, 0);
}

function roundMoney(n) {
  return Math.round(Number(n) * 100) / 100;
}

/**
 * Өндөрлөгөөний зарлага — банк дүн vs bucket-ийн POS хэсэг тусдаа;
 * POS SETTLEMENT орлоготой зөвхөн POS нэхэмжлэлийн нийлбэрийг харьцуулна.
 */
/** POS SETTLEMENT (банк D) vs системийн POS settle (ихэнхдээ D−1) */
const PosTPlusOneBanner = ({
  pageSummary,
  settlementIncoming,
  bucketPosSameDay,
  calendarDay,
}) => {
  const prev = pageSummary?.system?.prevDate;
  if (!prev?.date) return null;
  const incoming = parseFloat(settlementIncoming) || 0;
  const prevPos = parseFloat(prev.posSettleTotal) || 0;
  const t1Diff = roundMoney(incoming - prevPos);
  const t1Ok = Math.abs(t1Diff) < 1;
  const sameDayDiff = roundMoney(incoming - (parseFloat(bucketPosSameDay) || 0));
  const bankPosOut = parseFloat(pageSummary?.bank?.outgoingBreakdown?.pos);
  const posLabel =
    pageSummary?.accountNumber === "5570137868"
      ? "PURCHASE_POS"
      : "PAYMENT_TDB + PAYMENT_POS";

  if (t1Ok) return null;

  return (
    <Collapse
      ghost
      size="small"
      className="mb-2"
      items={[
        {
          key: "t1",
          label: (
            <span className="text-sm">
              POS тулгалт: зөрүү {fmt(t1Diff)} · банк {calendarDay} vs систем {prev.date}
            </span>
          ),
          children: (
            <div className="text-xs text-gray-600 space-y-1">
              <div>
                SETTLEMENT орлого {fmt(incoming)} · систем POS ({posLabel}) {fmt(prevPos)}
              </div>
              <div>
                Ижил өдрийн bucket POS {fmt(bucketPosSameDay)} (харьцуулахгүй) · зөрүү{" "}
                {fmt(sameDayDiff)}
                {Number.isFinite(bankPosOut) ? ` · summary POS гарц ${fmt(bankPosOut)}` : ""}
              </div>
            </div>
          ),
        },
      ]}
    />
  );
};

function InvoicePayoutTableSummary({
  rows,
  amountIndex,
  posIndex,
  settlementIncoming,
  labelPrefix = "",
  prevDayPosSettle,
  prevCalendarDay,
  calendarDay,
}) {
  const count = rows?.length || 0;
  if (count === 0) return null;
  const bankTotal = sumAbsAmount(rows);
  const posTotal = sumPosAmount(rows);
  const otherTotal = roundMoney(Math.max(0, bankTotal - posTotal));
  const incoming = parseFloat(settlementIncoming) || 0;
  const prevPos = parseFloat(prevDayPosSettle);
  const hasPrev = !labelPrefix && prevCalendarDay && Number.isFinite(prevPos);
  const t1Diff = hasPrev ? roundMoney(incoming - prevPos) : null;
  const t1Ok = t1Diff != null && Math.abs(t1Diff) < 0.01;
  const prefix = labelPrefix ? `${labelPrefix} · ` : "";

  return (
    <Table.Summary fixed>
      <Table.Summary.Row className="bg-gray-50">
        <Table.Summary.Cell index={0} colSpan={amountIndex}>
          <span className="text-xs font-semibold text-gray-700">
            {prefix}Нийт банк зарлага ({count} мөр)
          </span>
          <div className="text-[11px] text-gray-400 font-normal">QPay, багц, POS бүгд</div>
        </Table.Summary.Cell>
        <Table.Summary.Cell index={amountIndex}>
          <span style={{ color: "#dc2626", fontWeight: 600 }}>{fmt(bankTotal)}</span>
        </Table.Summary.Cell>
        {posIndex != null && (
          <Table.Summary.Cell index={posIndex}>
            <span className="text-gray-400 text-xs">→ доор</span>
          </Table.Summary.Cell>
        )}
        <Table.Summary.Cell index={(posIndex ?? amountIndex) + 1} />
      </Table.Summary.Row>
      <Table.Summary.Row className="bg-blue-50/80">
        <Table.Summary.Cell index={0} colSpan={amountIndex}>
          <span className="text-xs font-semibold text-blue-800">POS нэхэмжлэл (bucket)</span>
        </Table.Summary.Cell>
        <Table.Summary.Cell index={amountIndex}>
          <span className="text-gray-400 text-xs">—</span>
        </Table.Summary.Cell>
        {posIndex != null && (
          <Table.Summary.Cell index={posIndex}>
            <span style={{ color: "#2563eb", fontWeight: 600 }}>{fmt(posTotal)}</span>
          </Table.Summary.Cell>
        )}
        <Table.Summary.Cell index={(posIndex ?? amountIndex) + 1} />
      </Table.Summary.Row>
      <Table.Summary.Row className="bg-gray-50">
        <Table.Summary.Cell index={0} colSpan={amountIndex}>
          <span className="text-xs text-gray-600">Бусад канал (банк − POS)</span>
        </Table.Summary.Cell>
        <Table.Summary.Cell index={amountIndex}>
          <span style={{ color: "#9a3412", fontWeight: 500 }}>{fmt(otherTotal)}</span>
        </Table.Summary.Cell>
        {posIndex != null && (
          <Table.Summary.Cell index={posIndex}>
            <span className="text-gray-400 text-xs">—</span>
          </Table.Summary.Cell>
        )}
        <Table.Summary.Cell index={(posIndex ?? amountIndex) + 1} />
      </Table.Summary.Row>
      {hasPrev && !t1Ok && (
        <Table.Summary.Row className="bg-amber-50">
          <Table.Summary.Cell index={0} colSpan={amountIndex}>
            <span className="text-xs font-semibold">
              SETTLEMENT vs систем POS ({prevCalendarDay})
            </span>
            <div className="text-[10px] text-gray-500 font-normal mt-0.5">
              Дээрх POS багана нийлбэр биш — өмнөх өдрийн merchantSettle POS
            </div>
          </Table.Summary.Cell>
          <Table.Summary.Cell index={amountIndex} colSpan={posIndex != null ? 2 : 1}>
            <Tag color="orange" className="text-xs m-0">
              Зөрүү {fmt(t1Diff)}
            </Tag>
          </Table.Summary.Cell>
          <Table.Summary.Cell index={(posIndex ?? amountIndex) + 1} />
        </Table.Summary.Row>
      )}
    </Table.Summary>
  );
}

/** groupCols + expandable (antd expand багана index 0): Орлого=6, Холбогдсон=7, Зөрүү=8 */
const POS_SETT_GROUP_AMOUNT_IDX = 6;
const POS_SETT_GROUP_LINKED_IDX = 7;
const POS_SETT_GROUP_DIFF_IDX = 8;
const POS_SETT_GROUP_NOTE_IDX = 9;

function PosSettlementGroupsSummary({ groups, summary }) {
  const list = groups || [];
  const groupCount = list.length;
  if (groupCount === 0) return null;

  const totalIn =
    summary?.totalIncoming != null
      ? parseFloat(summary.totalIncoming)
      : list.reduce((s, g) => s + (parseFloat(g.amount) || 0), 0);
  const totalLinked =
    summary?.linkedOutgoing != null
      ? parseFloat(summary.linkedOutgoing)
      : list.reduce((s, g) => s + (parseFloat(g.linkedAmount) || 0), 0);
  const totalDiff =
    summary?.diff != null ? parseFloat(summary.diff) : roundMoney(totalIn - totalLinked);
  const diffOk = Math.abs(totalDiff) < 0.01;
  const incomingLines = summary?.incomingCount;

  return (
    <Table.Summary fixed>
      <Table.Summary.Row className="bg-gray-50">
        <Table.Summary.Cell index={0} />
        <Table.Summary.Cell index={1} colSpan={5}>
          <span className="text-xs font-semibold text-gray-700">
            Хөл дүн ({groupCount} бүлэг
            {incomingLines != null ? ` · ${incomingLines} SETTLEMENT мөр` : ""})
          </span>
        </Table.Summary.Cell>
        <Table.Summary.Cell index={POS_SETT_GROUP_AMOUNT_IDX} align="right">
          <span className="text-green-700 font-semibold tabular-nums">{fmt(totalIn)}</span>
        </Table.Summary.Cell>
        <Table.Summary.Cell index={POS_SETT_GROUP_LINKED_IDX} align="right">
          <span className="tabular-nums font-semibold">{fmt(totalLinked)}</span>
        </Table.Summary.Cell>
        <Table.Summary.Cell index={POS_SETT_GROUP_DIFF_IDX} align="right">
          <span
            className="tabular-nums font-semibold"
            style={{ color: diffOk ? "#16a34a" : "#dc2626" }}
          >
            {fmt(totalDiff)}
          </span>
        </Table.Summary.Cell>
        <Table.Summary.Cell index={POS_SETT_GROUP_NOTE_IDX} />
      </Table.Summary.Row>
    </Table.Summary>
  );
}

/** POS биш данс — зөвхөн банк + POS багана нийлбэр */
function OutgoingTableSummary({ rows, amountIndex, posIndex }) {
  const count = rows?.length || 0;
  if (count === 0) return null;
  const totalAmount = sumAbsAmount(rows);
  const totalPos = sumPosAmount(rows);
  return (
    <Table.Summary fixed>
      <Table.Summary.Row className="bg-gray-50 font-semibold">
        <Table.Summary.Cell index={0} colSpan={amountIndex}>
          Хөл дүн ({count} мөр)
        </Table.Summary.Cell>
        <Table.Summary.Cell index={amountIndex}>
          <span style={{ color: "#dc2626" }} title="Банк зарлага">
            {fmt(totalAmount)}
          </span>
        </Table.Summary.Cell>
        {posIndex != null && (
          <Table.Summary.Cell index={posIndex}>
            <span style={{ color: "#2563eb" }} title="Bucket POS хэсэг">
              {fmt(totalPos)}
            </span>
          </Table.Summary.Cell>
        )}
        <Table.Summary.Cell index={(posIndex ?? amountIndex) + 1} />
      </Table.Summary.Row>
    </Table.Summary>
  );
}

const OUTGOING_AMOUNT_COL_INDEX = 5;
const OUTGOING_POS_COL_INDEX = 6;

/** Tab бүрийн хүрээ: account = сонгосон банкны данс; system = merchantSettle/bucket (дансаар шүүж болно) */
const RECON_TAB_META = {
  bankLog: {
    label: "Дансны хуулга",
    scopeKind: "account",
    hint: (account, date) =>
      `Данс ${account}, өндөрлөгөний өдөр ${date} (BTL trnDate) — SETTLEMENT, bucket зарлага.`,
  },
  merchantSettle: {
    label: "Settle",
    scopeKind: "system",
    hint: (account, date) =>
      `merchantSettle settledAt = өндөрлөгөөний өдөр ${date}. Payout данс ${account} оролцсон batch.`,
  },
  failed: {
    label: "FAILED",
    scopeKind: "system",
    hint: (account, date) =>
      `Settle багц доторх FAILED / PENDING / CREATED гүйлгээ, ${date}, payout данс ${account}.`,
  },
  buckets: {
    label: "Bucket",
    scopeKind: "account",
    hint: (account, date) =>
      `Банк руу гарсан batch (transactionBucket), ${date}, гарах данс (fromAccountNumber) = ${account}.`,
  },
  trend: {
    label: "7 хоног (POS)",
    scopeKind: "account",
    hint: (account) =>
      `POS SETTLEMENT trend — зөвхөн payment/purchase POS дансууд (${account}).`,
  },
};

/** Tab дотор техник тайлбар — операторын горимд харагдахгүй */
const ReconciliationTabScope = () => null;

/** @param {string|number|null|undefined} amount @param {string} [hint] */
function operatorMoneyCell(amount, hint) {
  return (
    <div>
      <strong className="tabular-nums text-base">{fmt(amount)}</strong>
      {hint ? <div className="text-xs text-gray-500 mt-1 leading-snug max-w-xl">{hint}</div> : null}
    </div>
  );
}

/** POS payment данс: нийт = bucket хүснэгт + SETTLEMENT холбогдсон + бусад xPay */
function operatorBankOutgoingCell(summary, isPosAccount) {
  const total = summary?.bank?.outgoing;
  const baseHint =
    "Энэ өндөрлөгөөний өдөр банкны данснаас гарсан xPay зарлага (description ^xpay).";
  if (!isPosAccount) {
    return operatorMoneyCell(total, baseHint);
  }
  const pc = summary?.reconciliation?.posCheck;
  const bucketAmt = pc?.bucketPayoutAmount;
  const bucketCount = pc?.bucketPayoutCount;
  const linked = pc?.linkedOutgoing;
  const other = parseFloat(pc?.otherXpayOutgoing);
  const hasSplit =
    bucketAmt != null || linked != null || Number.isFinite(other);
  if (!hasSplit) {
    return operatorMoneyCell(total, baseHint);
  }
  const showOther = Number.isFinite(other) && Math.abs(other) >= 0.01;
  return (
    <div>
      <strong className="tabular-nums text-base">{fmt(total)}</strong>
      <ul className="text-xs text-gray-600 mt-1.5 mb-0 pl-4 list-disc leading-relaxed max-w-xl space-y-0.5">
        <li>
          Bucket / худалдаачид
          {bucketCount != null ? ` (${bucketCount} мөр)` : ""}:{" "}
          <span className="tabular-nums font-medium">{fmt(bucketAmt)}</span>
          <span className="text-gray-400"> — «Худалдаачдад гарсан» хүснэгт</span>
        </li>
        <li>
          SETTLEMENT холбогдсон:{" "}
          <span className="tabular-nums font-medium">{fmt(linked)}</span>
          <span className="text-gray-400"> — POS SETTLEMENT блок</span>
        </li>
        {showOther ? (
          <li>
            Бусад xPay:{" "}
            <span className="tabular-nums font-medium">{fmt(other)}</span>
          </li>
        ) : null}
      </ul>
    </div>
  );
}

/** Дээд хэсэг: статус + гол товч + огнооны товч тайлбар (Collapse) */
const ReconciliationOperatorHeader = ({
  account,
  accountType,
  settleDay,
  invoiceTxDay,
  invoiceSearchPath,
  isPosAccount,
  summary,
  summaryLoading,
  failedCount,
  posBalanced,
}) => {
  const bounds = summary?.dateBounds;
  const prevDay = summary?.system?.prevDate?.date;

  const reconcileStatusCell = () => {
    if (summaryLoading) return <Tag>Ачааллаж байна…</Tag>;
    if (failedCount > 0) {
      return (
        <Tag color="orange">
          FAILED {failedCount} · {fmt(summary?.reconciliation?.failedTransactionAmount)}
        </Tag>
      );
    }
    if (isPosAccount && posBalanced === false) {
      return (
        <span className="text-sm">
          <Tag color="gold">POS settlement</Tag>{" "}
          {summary?.reconciliation?.posCheck?.label || "Картын орлого — нэхэмжлэл биш"}
        </span>
      );
    }
    return <Tag color="green">Тэнцэл OK</Tag>;
  };

  const summaryTableRows = [
    {
      key: "account",
      label: "Данс",
      value: (
        <span>
          <strong>{account}</strong>
          {accountType ? ` (${accountType})` : ""}
        </span>
      ),
    },
    {
      key: "settleDay",
      label: "Өндөрлөгөөний өдөр",
      value: (
        <span>
          <strong>{settleDay || "—"}</strong>
          <span className="text-gray-500 text-xs ml-2">банк дээр мөнгө хөдөлсөн</span>
        </span>
      ),
    },
    {
      key: "invoiceDay",
      label: "Нэхэмжлэл хайх огноо",
      value: (
        <span className="flex flex-wrap items-center gap-2">
          <strong>{invoiceTxDay || "—"}</strong>
          <span className="text-gray-500 text-xs">гүйлгээний огноо (Гүйлгээний хяналт)</span>
          {invoiceTxDay && (
            <Link to={invoiceSearchPath}>
              <Button type="primary" size="small">
                Нээх
              </Button>
            </Link>
          )}
        </span>
      ),
    },
    {
      key: "status",
      label: "Төлөв",
      value: reconcileStatusCell(),
    },
  ];

  if (summary && !summaryLoading) {
    summaryTableRows.push({
      key: "bankOut",
      label: "Банкнаас гарсан",
      value: operatorBankOutgoingCell(summary, isPosAccount),
    });
    if (isPosAccount) {
      summaryTableRows.push({
        key: "posIn",
        label: "Картын орлого",
        value: operatorMoneyCell(
          summary?.bank?.incoming,
          "Банк SETTLEMENT (acquirer-ээс орсон). Зарлагатай нэмж бодохгүй — тусдаа мөр."
        ),
      });
    }

    const fb = summary?.system?.feeBreakdown;
    if (fb && Number(fb.transactionCount) > 0) {
      summaryTableRows.push(
        {
          key: "feeHdr",
          label: "Шимтгэл (систем)",
          value: (
            <span className="text-xs text-gray-500">
              Энэ өндөрлөгөөний merchantSettle-ийн SETTLED transaction (
              {fb.transactionCount} гүйлгээ). invoice → invoiceItem → transaction.
            </span>
          ),
        },
        {
          key: "feeGross",
          label: "Төлбөрийн дүн",
          value: operatorMoneyCell(
            fb.grossAmount,
            "Хэрэглэгч төлсөн бүтэн дүн (transaction.amount)."
          ),
        },
        {
          key: "feePayout",
          label: "→ afterFeeAmount (дэд мерчант)",
          value: operatorMoneyCell(
            fb.merchantPayout,
            "transaction.afterFeeAmount — дэд мерчант (VAT) данс руу bucket-аар шилжүүлсэн payout."
          ),
        },
        {
          key: "feeXpay",
          label: "→ SmartBilling / xPay",
          value: operatorMoneyCell(
            fb.xpayFee,
            "xpayFee — платформын хуваалт (ихэнхдээ income гарц руу)."
          ),
        },
        {
          key: "feeMerch",
          label: "→ merchantFee",
          value: operatorMoneyCell(
            fb.merchantFee,
            "transaction.merchantFee — invoice үүсгэгч (эзэмшигч) мерчантын хуваалт."
          ),
        },
        {
          key: "feeProc",
          label: "→ Карт/QPay боловсруулалт",
          value: operatorMoneyCell(
            fb.processingFee,
            "processingFee — сувгийн шимтгэл (merchantSettle.processingAmount-тай ойролцоо)."
          ),
        },
        {
          key: "feeIncome",
          label: "Income гарц (шимтгэл)",
          value: operatorMoneyCell(
            summary?.system?.incomeTotal,
            "SETTLED incomeTransaction — ихэнхдээ xPay шимтгэлийг банкнаас шилжүүлсэн."
          ),
        }
      );
    }
  }

  return (
    <Card className="mb-4" styles={{ body: { padding: "16px 20px" } }} title="Өндөрлөгөөний тулгалт">
      <Table
        size="small"
        pagination={false}
        showHeader={false}
        bordered
        rowKey="key"
        columns={[
          {
            dataIndex: "label",
            key: "label",
            width: 200,
            className: "text-sm text-gray-600 bg-gray-50/80 font-medium",
          },
          {
            dataIndex: "value",
            key: "value",
            className: "text-sm",
          },
        ]}
        dataSource={summaryTableRows}
      />
      <Collapse
        ghost
        size="small"
        className="mt-2 bg-gray-50 rounded-lg"
        items={[
          {
            key: "help",
            label: <span className="text-sm text-gray-600">Огноо, tab — товч тайлбар</span>,
            children: (
              <ul className="text-xs text-gray-600 list-disc pl-4 mb-0 space-y-1">
                <li>
                  <strong>Дансны хуулга</strong> — SETTLEMENT + bucket зарлага (xPay settle), энэ данс.
                </li>
                <li>
                  <strong>Settle / FAILED / Bucket</strong> — системийн batch; FAILED tab-д
                  алдаатай гүйлгээ.
                </li>
                {isPosAccount && prevDay && (
                  <li>
                    POS: картын төлбөр ихэвч <strong>{prevDay}</strong>, банк орлого{" "}
                    <strong>{settleDay}</strong>.
                  </li>
                )}
                {bounds && (
                  <li className="text-gray-400">
                    trnDate {bounds.trnDateFrom} — {bounds.trnDateTo} ({bounds.timezone})
                  </li>
                )}
              </ul>
            ),
          },
        ]}
      />
    </Card>
  );
};

const StatCard = ({ title, value, color, icon, loading, sub }) => (
  <Card
    size="small"
    style={{ borderTop: `3px solid ${color}`, flex: 1, minWidth: 140 }}
    styles={{ body: { padding: "12px 16px" } }}
  >
    {loading ? (
      <Skeleton active paragraph={{ rows: 1 }} title={false} />
    ) : (
      <div className="flex items-center gap-3">
        <div style={{ color, fontSize: 22 }}>{icon}</div>
        <div>
          <div className="text-xs text-gray-400">{title}</div>
          <div className="font-bold text-sm" style={{ color }}>{value}</div>
          {sub && <div className="text-xs text-gray-400 mt-0.5">{sub}</div>}
        </div>
      </div>
    )}
  </Card>
);

// ─── POS Тэнцэл задаргаа ──────────────────────────────────────────────────────
const ReconciliationDetail = ({ summary, loading }) => {
  if (loading) return <Skeleton active paragraph={{ rows: 3 }} className="mb-4" />;
  if (!summary) return null;

  const posCheck = summary.reconciliation?.posCheck;
  const posBalanced = posCheck?.isBalanced;
  const posDiff = parseFloat(posCheck?.diff || 0);

  const Row = ({ label, value, color, bold }) => (
    <div className="flex flex-col items-center px-4">
      <div className="text-xs text-gray-400 mb-1 whitespace-nowrap">{label}</div>
      <div
        className={bold ? "font-bold text-sm" : "text-sm"}
        style={{ color: color || "#111" }}
      >
        {value}
      </div>
    </div>
  );

  return (
    <Card
      size="small"
      className="mb-4"
      styles={{ body: { padding: "12px 16px" } }}
      title={
        <div className="flex items-center gap-2 text-sm">
          <span className="font-semibold">POS Тэнцэл шалгалт</span>
          {posCheck && (
            <Tag color={posBalanced ? "success" : "error"} className="ml-1">
              {posBalanced ? "Тэнцсэн ✓" : `Зөрүү: ${fmt(Math.abs(posDiff))}`}
            </Tag>
          )}
          {posCheck?.note && (
            <span className="text-xs text-gray-400 font-normal">{posCheck.note}</span>
          )}
        </div>
      }
    >
      {/* POS Check row */}
      <div className="flex items-center flex-wrap gap-0 divide-x divide-gray-200 mb-3">
        <Row
          label={`Банкны SETTLEMENT (${summary.settleDate})`}
          value={fmt(posCheck?.bankIncoming)}
          color="#2563eb"
          bold
        />
        <Row
          label="Холбогдсон зарлага"
          value={fmt(posCheck?.linkedOutgoing)}
          color="#374151"
          bold
        />
        <Row
          label="Зөрүү"
          value={fmt(posCheck?.diff)}
          color={posBalanced ? "#16a34a" : "#dc2626"}
          bold
        />
        <div className="px-4 flex flex-col items-center">
          <div className="text-xs text-gray-400 mb-1">Статус</div>
          {posBalanced ? (
            <CheckCircleOutlined style={{ color: "#16a34a", fontSize: 20 }} />
          ) : (
            <CloseCircleOutlined style={{ color: "#dc2626", fontSize: 20 }} />
          )}
        </div>
      </div>

      {posCheck?.label && (
        <div className="text-xs text-gray-500 mt-2">{posCheck.label}</div>
      )}
    </Card>
  );
};

// ─── Tab 1: Bank Log ───────────────────────────────────────────────────────────
const BankLogTab = ({
  date,
  account,
  isPosAccount,
  tabKey = "bankLog",
  pageSummary = null,
}) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!date || !account) return;
    setLoading(true);
    const res = await get(`/reconciliation/bank-log?date=${date}&account=${account}`);
    const d = res?.data || null;
    if (d) {
      d.groups = Array.isArray(d.groups) ? d.groups : [];
      d.unrelated = Array.isArray(d.unrelated) ? d.unrelated : [];
    }
    setData(d);
    setLoading(false);
  }, [date, account]);

  useEffect(() => { load(); }, [load]);

  const cols = (sign, extra = [], afterAmount = []) => [
    { title: "ID", dataIndex: "id", key: "id", width: 80, className: "text-xs" },
    {
      title: "Тайлбар",
      dataIndex: "description",
      key: "description",
      className: "text-xs",
      render: (v) => {
        const match = v?.match(/#(\d+)#/);
        if (match) {
          return (
            <span>
              {v.replace(match[0], "")}{" "}
              <Tag color="blue" className="text-xs">#{match[1]}#</Tag>
            </span>
          );
        }
        return <span>{v}</span>;
      },
    },
    ...extra,
    {
      title: "Дүн",
      dataIndex: "amount",
      key: "amount",
      width: 130,
      className: "text-xs",
      render: (v) => (
        <span style={{ color: sign > 0 ? "#16a34a" : "#dc2626", fontWeight: 600 }}>
          {fmt(Math.abs(parseFloat(v)))}
        </span>
      ),
    },
    ...afterAmount,
    {
      title: "Огноо",
      dataIndex: "trnDate",
      key: "trnDate",
      width: 110,
      className: "text-xs",
      render: (v) => v ? dayjs(v).format("YYYY-MM-DD") : "-",
    },
  ];

  const destinationCols = [
    {
      title: "Дэд мерчант",
      dataIndex: "vatMerchantName",
      key: "vatMerchantName",
      className: "text-xs",
      render: (v) => v || <span className="text-gray-400">-</span>,
    },
    {
      title: "Эзэмшигч",
      dataIndex: "ownerName",
      key: "ownerName",
      className: "text-xs",
      render: (v) => v || <span className="text-gray-400">-</span>,
    },
    {
      title: "Очих данс",
      key: "destAccount",
      className: "text-xs",
      render: (_, row) =>
        row.accountNumber || row.relatedAccount || (
          <span className="text-gray-400">-</span>
        ),
    },
  ];

  const posAmountCols = [
    {
      title: (
        <Tooltip title="Bucket доторх PAYMENT_POS / PAYMENT_TDB гүйлгээний afterFee нийлбэр. Банкны «Дүн»-ээс тусдаа.">
          POS дүн
        </Tooltip>
      ),
      dataIndex: "posAmount",
      key: "posAmount",
      width: 140,
      className: "text-xs",
      render: (v, row) => {
        if (v == null) return <span className="text-gray-400">-</span>;
        const pos = parseFloat(v) || 0;
        const bankAbs = Math.abs(parseFloat(row.amount) || 0);
        const allPosBucket = pos > 0 && Math.abs(pos - bankAbs) < 0.01;
        const countHint =
          row.txCount != null && row.posCount != null
            ? `${row.posCount}/${row.txCount} txn`
            : null;
        return (
          <Tooltip
            title={
              row.txCount
                ? `Bucket #${row.transactionBucketId || "?"}: POS ${row.posCount}/${row.txCount} гүйлгээ · QPay/бусад ${fmt(row.otherAmount)}`
                : "POS гүйлгээ олдсонгүй (QPay/багц bucket)"
            }
          >
            <div className="leading-tight">
              <span
                style={{ color: pos > 0 ? "#2563eb" : undefined, fontWeight: pos > 0 ? 600 : 400 }}
              >
                {fmt(v)}
              </span>
              {countHint && (
                <div className="text-[10px] text-gray-500">{countHint}</div>
              )}
              {allPosBucket && (
                <div className="text-[10px] text-blue-600">бүхэл bucket POS</div>
              )}
            </div>
          </Tooltip>
        );
      },
    },
  ];

  const groupCols = [
    { title: "Нэр", dataIndex: "name", key: "name", className: "text-xs" },
    {
      title: "Дэд мерчант",
      key: "vat",
      className: "text-xs",
      render: (_, row) => row.poses?.[0]?.vatMerchantName || row.registrationLabel,
    },
    {
      title: "Эзэмшигч",
      key: "owner",
      className: "text-xs",
      render: (_, row) => row.poses?.[0]?.ownerName || "-",
    },
    {
      title: "Очих данс",
      key: "account",
      className: "text-xs",
      render: (_, row) => row.poses?.[0]?.accountNumber || "-",
    },
    {
      title: "Төлөв",
      key: "status",
      width: 110,
      render: (_, row) => {
        const status = row.poses?.[0]?.status;
        if (status === "ACTIVE") return <Tag color="cyan">Идэвхтэй</Tag>;
        if (status === "INACTIVE") return <Tag>Идэвхгүй</Tag>;
        if (status === "DELETED") return <Tag color="red">Устгасан</Tag>;
        return <Tag color="orange">Бүртгэлгүй</Tag>;
      },
    },
    {
      title: "Орлого",
      dataIndex: "amount",
      key: "amount",
      width: 120,
      render: (v) => <span className="text-green-700">{fmt(v)}</span>,
    },
    {
      title: "Холбогдсон зарлага",
      dataIndex: "linkedAmount",
      key: "linkedAmount",
      width: 140,
      render: (v) => fmt(v),
    },
    {
      title: "Зөрүү",
      dataIndex: "diff",
      key: "diff",
      width: 120,
      render: (v) => fmt(v),
    },
    {
      title: "Тэмдэглэл",
      dataIndex: "label",
      key: "label",
      className: "text-xs",
      render: (v) => v || "-",
    },
  ];

  const unrelatedCols = cols(-1, destinationCols, posAmountCols);
  const linkedLineCols = cols(-1, destinationCols, posAmountCols);

  if (loading) return <Skeleton active />;
  if (!data) return null;

  const settlementIncoming = data.summary?.totalIncoming;
  const prevMeta = pageSummary?.system?.prevDate;
  const prevDayPosSettle = prevMeta?.posSettleTotal;
  const prevCalendarDay = prevMeta?.date;
  const bucketPosSameDay = sumPosAmount(data.unrelated);

  const posSettlementBlock = isPosAccount ? (
    <div>
      <div className="font-medium text-gray-800 mb-2">
        Картын орлого (банк SETTLEMENT): {fmt(data.summary?.totalIncoming)}
        <span className="text-gray-400 font-normal text-sm ml-2">
          {data.summary?.incomingCount} мөр
        </span>
      </div>
      <PosTPlusOneBanner
        pageSummary={pageSummary}
        settlementIncoming={settlementIncoming}
        bucketPosSameDay={bucketPosSameDay}
        calendarDay={data.date}
      />
      <Table
        size="small"
        columns={groupCols}
        dataSource={data.groups || []}
        rowKey="name"
        pagination={false}
        bordered
        summary={() => (
          <PosSettlementGroupsSummary groups={data.groups} summary={data.summary} />
        )}
        expandable={{
          expandedRowRender: (group) => (
            <div className="flex flex-col gap-3">
              <Table
                size="small"
                columns={cols(1)}
                dataSource={group.lines || []}
                rowKey="id"
                pagination={false}
              />
              {group.linkedLines?.length > 0 && (
                <div>
                  <div className="text-xs text-gray-500 mb-1">
                    Нэрээр холбогдсон зарлага (POS SETTLEMENT-тай ижил бүлэг)
                  </div>
                  <Table
                    size="small"
                    columns={linkedLineCols}
                    dataSource={group.linkedLines}
                    rowKey="id"
                    pagination={false}
                    summary={() => (
                      <InvoicePayoutTableSummary
                        rows={group.linkedLines}
                        amountIndex={OUTGOING_AMOUNT_COL_INDEX}
                        posIndex={OUTGOING_POS_COL_INDEX}
                        settlementIncoming={group.amount}
                        labelPrefix={group.name}
                        calendarDay={data.date}
                      />
                    )}
                  />
                </div>
              )}
            </div>
          ),
        }}
      />
    </div>
  ) : null;

  const invoicePayoutBlock = (
    <div>
      <div className="font-semibold text-gray-700 mb-2 flex items-center gap-2 flex-wrap">
        {isPosAccount ? "Худалдаачдад гарсан (bucket)" : "Зарлагын мөрүүд"}
        <span className="text-sm font-normal text-gray-500 ml-2">
          {data.summary?.unrelatedCount ?? (data.unrelated?.length || 0)} мөр ·{" "}
          {fmt(data.summary?.unrelatedAmount ?? sumAbsAmount(data.unrelated))}
        </span>
      </div>
      <Table
        size="small"
        columns={unrelatedCols}
        dataSource={data.unrelated || []}
        rowKey="id"
        pagination={false}
        bordered
        summary={() =>
          isPosAccount ? (
            <InvoicePayoutTableSummary
              rows={data.unrelated}
              amountIndex={OUTGOING_AMOUNT_COL_INDEX}
              posIndex={OUTGOING_POS_COL_INDEX}
              settlementIncoming={settlementIncoming}
              prevDayPosSettle={prevDayPosSettle}
              prevCalendarDay={prevCalendarDay}
              calendarDay={data.date}
            />
          ) : (
            <OutgoingTableSummary
              rows={data.unrelated}
              amountIndex={OUTGOING_AMOUNT_COL_INDEX}
              posIndex={OUTGOING_POS_COL_INDEX}
            />
          )
        }
      />
    </div>
  );

  return (
    <div className="flex flex-col gap-4">
      <ReconciliationTabScope tabKey={tabKey} account={account} date={date} />
      {posSettlementBlock}
      {invoicePayoutBlock}
    </div>
  );
};

// ─── Tab 2: MerchantSettle ────────────────────────────────────────────────────
const MerchantSettleTab = ({ date, account }) => {
  const [data, setData] = useState([]);
  const [settleSummary, setSettleSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [failedModal, setFailedModal] = useState(null);
  const [failedData, setFailedData] = useState(null);
  const [failedLoading, setFailedLoading] = useState(false);

  const load = useCallback(async () => {
    if (!date) return;
    setLoading(true);
    const q = account
      ? `/reconciliation/merchant-settle?date=${date}&account=${account}`
      : `/reconciliation/merchant-settle?date=${date}`;
    const res = await get(q);
    const settles = Array.isArray(res?.data?.settles) ? res.data.settles : [];
    setData(settles);
    setSettleSummary(res?.data?.summary || null);
    setLoading(false);
  }, [date, account]);

  useEffect(() => { load(); }, [load]);

  const openFailed = async (record) => {
    setFailedModal(record);
    setFailedLoading(true);
    const res = await get(`/reconciliation/failed-transactions?merchantSettleId=${record.id}`);
    const fd = res?.data || null;
    if (fd) fd.transactions = Array.isArray(fd.transactions) ? fd.transactions : [];
    setFailedData(fd);
    setFailedLoading(false);
  };

  const cols = [
    { title: "ID", dataIndex: "id", key: "id", width: 70, className: "text-xs" },
    { title: "Merchant ID", dataIndex: "merchantId", key: "merchantId", width: 90, className: "text-xs" },
    {
      title: "Settle дүн",
      dataIndex: "settleAmount",
      key: "settleAmount",
      className: "text-xs",
      render: (v) => <span className="font-semibold">{fmt(v)}</span>,
    },
    {
      title: "Settled тоо",
      key: "settledCount",
      className: "text-xs",
      render: (_, r) => r.transactions?.settledCount ?? "-",
    },
    {
      title: "Failed тоо",
      key: "failedCount",
      className: "text-xs",
      render: (_, r) => (
        <Tag color={r.transactions?.failedCount > 0 ? "red" : "default"}>
          {r.transactions?.failedCount ?? 0}
        </Tag>
      ),
    },
    {
      title: "Settled дүн",
      key: "settledAmount",
      className: "text-xs",
      render: (_, r) => fmt(r.transactions?.settledAmount),
    },
    {
      title: "Failed дүн",
      key: "failedAmount",
      className: "text-xs",
      render: (_, r) => (
        <span style={{ color: r.transactions?.failedCount > 0 ? "#dc2626" : undefined }}>
          {fmt(r.transactions?.failedAmount)}
        </span>
      ),
    },
    {
      title: "Зөрүү",
      dataIndex: "difference",
      key: "difference",
      className: "text-xs",
      render: (v) => (
        <span style={{ color: parseFloat(v) !== 0 ? "#dc2626" : "#16a34a", fontWeight: 600 }}>
          {fmt(v)}
        </span>
      ),
    },
    {
      title: "",
      key: "action",
      width: 80,
      render: (_, r) =>
        r.hasProblem ? (
          <Button size="small" danger onClick={() => openFailed(r)}>FAILED</Button>
        ) : (
          <Button size="small" onClick={() => openFailed(r)}>Харах</Button>
        ),
    },
  ];

  const failedCols = [
    { title: "ID", dataIndex: "id", key: "id", width: 70, className: "text-xs" },
    {
      title: "Статус",
      dataIndex: "status",
      key: "status",
      width: 90,
      className: "text-xs",
      render: (v) => <Tag color="red">{v}</Tag>,
    },
    { title: "Данс", dataIndex: "accountNumber", key: "accountNumber", className: "text-xs" },
    { title: "Дүн", dataIndex: "amount", key: "amount", className: "text-xs", render: (v) => fmt(v) },
    {
      title: "Шимтгэлийн дараа",
      dataIndex: "afterFeeAmount",
      key: "afterFeeAmount",
      className: "text-xs",
      render: (v) => fmt(v),
    },
  ];

  return (
    <>
      <ReconciliationTabScope tabKey="merchantSettle" account={account} date={date} />
      {settleSummary && (
        <div className="mb-3 flex gap-3 flex-wrap">
          <Tag color="blue">Нийт settle: {settleSummary.settleCount}</Tag>
          <Tag color="blue">Нийт дүн: {fmt(settleSummary.totalSettleAmount)}</Tag>
          <Tag color={parseFloat(settleSummary.totalFailedAmount) > 0 ? "red" : "default"}>
            FAILED дүн: {fmt(settleSummary.totalFailedAmount)}
          </Tag>
          <Tag color={settleSummary.problemCount > 0 ? "red" : "green"}>
            Асуудалтай: {settleSummary.problemCount}
          </Tag>
        </div>
      )}
      <Table
        size="small"
        columns={cols}
        dataSource={data}
        rowKey="id"
        loading={loading}
        pagination={{ pageSize: 20 }}
        bordered
        rowClassName={(r) => (r.hasProblem ? "bg-red-50" : "")}
      />
      <Modal
        open={!!failedModal}
        onCancel={() => { setFailedModal(null); setFailedData(null); }}
        footer={null}
        title={`Merchant #${failedModal?.merchantId} — FAILED гүйлгээнүүд`}
        width={700}
      >
        {failedLoading ? (
          <Skeleton active />
        ) : (
          <>
            {failedData && (
              <div className="mb-3 flex gap-4">
                <Tag color="red">Тоо: {failedData.count}</Tag>
                <Tag color="red">Нийт дүн: {fmt(failedData.totalAmount)}</Tag>
              </div>
            )}
            <Table
              size="small"
              columns={failedCols}
              dataSource={failedData?.transactions || []}
              rowKey="id"
              pagination={false}
              bordered
            />
          </>
        )}
      </Modal>
    </>
  );
};

// ─── Tab 3: Failed Transactions ───────────────────────────────────────────────
const FailedTab = ({ date, account }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!date) return;
    setLoading(true);
    const q = account
      ? `/reconciliation/failed-transactions?date=${date}&account=${account}`
      : `/reconciliation/failed-transactions?date=${date}`;
    const res = await get(q);
    const fd = res?.data || null;
    if (fd) fd.transactions = Array.isArray(fd.transactions) ? fd.transactions : [];
    setData(fd);
    setLoading(false);
  }, [date, account]);

  useEffect(() => { load(); }, [load]);

  const cols = [
    { title: "ID", dataIndex: "id", key: "id", width: 70, className: "text-xs" },
    { title: "Settle ID", dataIndex: "merchantSettleId", key: "merchantSettleId", width: 90, className: "text-xs" },
    {
      title: "Статус",
      dataIndex: "status",
      key: "status",
      width: 90,
      className: "text-xs",
      render: (v) => <Tag color="red">{v}</Tag>,
    },
    { title: "Данс", dataIndex: "accountNumber", key: "accountNumber", className: "text-xs" },
    { title: "Дүн", dataIndex: "amount", key: "amount", className: "text-xs", render: (v) => fmt(v) },
    {
      title: "Шимтгэлийн дараа",
      dataIndex: "afterFeeAmount",
      key: "afterFeeAmount",
      className: "text-xs",
      render: (v) => fmt(v),
    },
    {
      title: "Огноо",
      dataIndex: "updatedAt",
      key: "updatedAt",
      className: "text-xs",
      render: (v) => v ? dayjs(v).format("YYYY-MM-DD HH:mm") : "-",
    },
  ];

  if (loading) return <Skeleton active />;

  return (
    <div>
      <ReconciliationTabScope tabKey="failed" account={account} date={date} />
      {data && (
        <div className="mb-3 flex gap-4">
          <Tag color="red" className="text-sm">Нийт тоо: {data.count}</Tag>
          <Tag color="red" className="text-sm">Нийт дүн: {fmt(data.totalAmount)}</Tag>
        </div>
      )}
      <Table
        size="small"
        columns={cols}
        dataSource={data?.transactions || []}
        rowKey="id"
        pagination={{ pageSize: 20 }}
        bordered
      />
    </div>
  );
};

// ─── Tab 4: Buckets ───────────────────────────────────────────────────────────
const BucketsTab = ({ date, account }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!date) return;
    setLoading(true);
    const q = account
      ? `/reconciliation/buckets?date=${date}&account=${account}`
      : `/reconciliation/buckets?date=${date}`;
    const res = await get(q);
    const bd = res?.data || null;
    if (bd) bd.byAccount = Array.isArray(bd.byAccount) ? bd.byAccount : [];
    setData(bd);
    setLoading(false);
  }, [date, account]);

  useEffect(() => { load(); }, [load]);

  const summaryCols = [
    { title: "Данс", dataIndex: "fromAccountNumber", key: "fromAccountNumber", className: "text-xs" },
    { title: "Тоо", dataIndex: "count", key: "count", width: 70, className: "text-xs" },
    {
      title: "Нийт дүн",
      dataIndex: "totalAmount",
      key: "totalAmount",
      className: "text-xs",
      render: (v) => fmt(v),
    },
    {
      title: "Нийт шимтгэл",
      dataIndex: "totalFee",
      key: "totalFee",
      className: "text-xs",
      render: (v) => fmt(v),
    },
  ];

  if (loading) return <Skeleton active />;

  const tableTitle = account
    ? `Гарах данс ${account} (fromAccountNumber)`
    : "Бүх гарах дансаар бүлэглэсэн";

  return (
    <div>
      <ReconciliationTabScope tabKey="buckets" account={account} date={date} />
      {data?.summary && (
        <div className="mb-3 flex gap-4 flex-wrap">
          <Tag color="blue" className="text-sm">Bucket тоо: {data.summary.bucketCount}</Tag>
          <Tag color="blue" className="text-sm">Нийт: {fmt(data.summary.grandTotal)}</Tag>
          <Tag color="blue" className="text-sm">Шимтгэл: {fmt(data.summary.grandFee)}</Tag>
        </div>
      )}
      <Table
        size="small"
        columns={summaryCols}
        dataSource={data?.byAccount || []}
        rowKey="fromAccountNumber"
        pagination={false}
        bordered
        title={() => <span className="font-semibold">{tableTitle}</span>}
        locale={{
          emptyText: account
            ? "Энэ дансаас гарсан bucket байхгүй"
            : "Bucket байхгүй",
        }}
      />
    </div>
  );
};

// ─── Tab 5: Trend ─────────────────────────────────────────────────────────────
const TrendTab = ({ account }) => {
  const [data, setData] = useState([]);
  const [rangeMeta, setRangeMeta] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!account) return;
    setLoading(true);
    const res = await get(`/reconciliation/trend?days=7&account=${account}`);
    const payload = res?.data;
    const trend = payload?.trend;
    setData(Array.isArray(trend) ? trend : []);
    setRangeMeta(
      payload
        ? {
            fromDate: payload.fromDate,
            toDate: payload.toDate,
            days: payload.days,
            timezone: payload.timezone,
          }
        : null
    );
    setLoading(false);
  }, [account]);

  useEffect(() => { load(); }, [load]);

  const cols = [
    { title: "Огноо", dataIndex: "date", key: "date", className: "text-xs font-semibold" },
    {
      title: "Банкнаас орсон",
      dataIndex: "incoming",
      key: "incoming",
      className: "text-xs",
      render: (v) => <span className="text-blue-600 font-semibold">{fmt(v)}</span>,
    },
    {
      title: "Холбогдсон зарлага",
      dataIndex: "outgoing",
      key: "outgoing",
      className: "text-xs",
      render: (v) => <span className="text-green-600 font-semibold">{fmt(v)}</span>,
    },
    {
      title: "Үлдэгдэл (net)",
      dataIndex: "netKept",
      key: "netKept",
      className: "text-xs",
      render: (v) => <span className="text-emerald-700 font-bold">{fmt(v)}</span>,
    },
    { title: "Орж ирсэн тоо", dataIndex: "incomingCount", key: "incomingCount", width: 110, className: "text-xs" },
    { title: "Гарсан тоо", dataIndex: "outgoingCount", key: "outgoingCount", width: 100, className: "text-xs" },
  ];

  if (loading) return <Skeleton active />;

  return (
    <div>
      <ReconciliationTabScope tabKey="trend" account={account} date={rangeMeta?.toDate} />
      {rangeMeta?.fromDate && rangeMeta?.toDate && (
        <div className="text-xs text-gray-600 mb-3 px-2 py-2 bg-gray-50 border border-gray-100 rounded-lg">
          <span className="font-medium">Trend хугацаа (УБ): </span>
          <Tag className="text-xs">{rangeMeta.fromDate}</Tag>
          <span className="mx-1">→</span>
          <Tag className="text-xs">{rangeMeta.toDate}</Tag>
          <span className="text-gray-500 ml-2">
            {rangeMeta.days} өдөр
            {rangeMeta.timezone ? ` · ${rangeMeta.timezone}` : ""}
          </span>
        </div>
      )}
      <Table
        size="small"
        columns={cols}
        dataSource={data}
        rowKey="date"
        pagination={false}
        bordered
      />
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const Reconciliation = () => {
  const [accounts, setAccounts] = useState([]);
  const [account, setAccount] = useState("5570025382");
  const [date, setDate] = useState(dayjs().format("YYYY-MM-DD"));
  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [error, setError] = useState(null);

  const loadAccounts = async () => {
    const res = await get("/reconciliation/accounts");
    if (res?.data) {
      setAccounts(res.data);
      if (res.data.length > 0 && !account) setAccount(res.data[0].accountNumber);
    }
  };

  const loadSummary = useCallback(async () => {
    if (!date || !account) return;
    setSummaryLoading(true);
    setError(null);
    const res = await get(`/reconciliation/summary?settle_date=${date}&account=${account}`);
    if (res?.data) {
      setSummary(res.data);
    } else {
      setError("Мэдээлэл ачааллахад алдаа гарлаа.");
    }
    setSummaryLoading(false);
  }, [date, account]);

  useEffect(() => { loadAccounts(); }, []);
  useEffect(() => { loadSummary(); }, [loadSummary]);

  const posBalanced = summary?.reconciliation?.posCheck?.isBalanced;
  const failedCount = summary?.reconciliation?.failedTransactionCount || 0;
  const isPosAccount = isPosReconciliationAccount(account);
  const accountMeta = accounts.find((a) => a.accountNumber === account);
  const invoiceTxDay = invoiceTxDayFromSettleDate(date, {
    isPosAccount,
    prevSettleDay: summary?.system?.prevDate?.date,
  });
  const invoiceSearchPath = adminInvoiceTxSearchPath(invoiceTxDay);

  const tabItems = [
    {
      key: "bankLog",
      label: RECON_TAB_META.bankLog.label,
      children: (
        <BankLogTab
          date={date}
          account={account}
          isPosAccount={isPosAccount}
          tabKey="bankLog"
          pageSummary={summary}
        />
      ),
    },
    {
      key: "merchantSettle",
      label: RECON_TAB_META.merchantSettle.label,
      children: <MerchantSettleTab date={date} account={account} />,
    },
    {
      key: "failed",
      label: (
        <Badge count={failedCount} size="small" offset={[6, 0]}>
          {RECON_TAB_META.failed.label}
        </Badge>
      ),
      children: <FailedTab date={date} account={account} />,
    },
    {
      key: "buckets",
      label: "Bucket гарц",
      children: <BucketsTab date={date} account={account} />,
    },
    ...(isPosAccount
      ? [
          {
            key: "trend",
            label: "7 хоног",
            children: <TrendTab account={account} />,
          },
        ]
      : []),
  ];

  return (
    <div style={{ padding: 16 }}>
      {/* Header controls */}
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div className="flex items-center gap-3 flex-wrap">
          <span className="text-gray-500 text-sm">Данс:</span>
          <Select
            value={account}
            onChange={setAccount}
            style={{ width: 220 }}
            size="small"
            options={
              accounts.length > 0
                ? accounts.map((a) => ({
                    value: a.accountNumber,
                    label: `${a.accountNumber} (${a.type || "—"})`,
                  }))
                : [{ value: "5570025382", label: "5570025382 (payment)" }]
            }
          />
        </div>
        <div className="flex items-center gap-2">
          <Tooltip title="Банкны хуулга (trnDate), merchantSettle settledAt, bucket — өндөрлөгөөний өдөр. Нэхэмжлэлийн гүйлгээний огноо биш.">
            <div className="flex flex-col items-end">
              <span className="text-[11px] text-gray-500 leading-none mb-1">
                Өндөрлөгөөний өдөр
              </span>
              <DatePicker
                value={dayjs(date)}
                onChange={(d) => d && setDate(d.format("YYYY-MM-DD"))}
                format="YYYY-MM-DD"
                size="small"
                allowClear={false}
              />
            </div>
          </Tooltip>
          <Tooltip title="Шинэчлэх">
            <Button
              size="small"
              icon={<ReloadOutlined />}
              onClick={loadSummary}
              loading={summaryLoading}
            />
          </Tooltip>
        </div>
      </div>

      <ReconciliationOperatorHeader
        account={account}
        accountType={accountMeta?.type}
        settleDay={summary?.dateBounds?.calendarDay || date}
        invoiceTxDay={invoiceTxDay}
        invoiceSearchPath={invoiceSearchPath}
        isPosAccount={isPosAccount}
        summary={summary}
        summaryLoading={summaryLoading}
        failedCount={failedCount}
        posBalanced={posBalanced}
      />

      {error && (
        <Alert
          message={error}
          type="error"
          showIcon
          action={<Button size="small" onClick={loadSummary}>Дахин оролдох</Button>}
          className="mb-2"
        />
      )}

      <Collapse
        ghost
        className="mb-4"
        items={[
          {
            key: "stats",
            label: <span className="text-sm text-gray-600">Дэлгэрэнгүй тоо (банк, систем)</span>,
            children: (
              <>
                <div className="flex gap-3 mb-3 flex-wrap">
                  {isPosAccount ? (
                    <>
                      <StatCard
                        title="Банк орлого"
                        value={fmt(summary?.bank?.incoming)}
                        color="#2563eb"
                        icon={<BankOutlined />}
                        loading={summaryLoading}
                      />
                      <StatCard
                        title="Холбогдсон зарлага"
                        value={fmt(summary?.reconciliation?.posCheck?.linkedOutgoing)}
                        color="#16a34a"
                        icon={<BankOutlined />}
                        loading={summaryLoading}
                      />
                      <StatCard
                        title="POS зөрүү"
                        value={fmt(summary?.reconciliation?.posCheck?.diff)}
                        color="#059669"
                        icon={<CheckCircleOutlined />}
                        loading={summaryLoading}
                      />
                      <StatCard
                        title="Bucket (бүх данс)"
                        value={fmt(summary?.system?.bucketTotal)}
                        color="#374151"
                        icon={<BankOutlined />}
                        loading={summaryLoading}
                        sub="SETTLED batch — техник"
                      />
                      <StatCard
                        title="Income (бүх данс)"
                        value={fmt(summary?.system?.incomeTotal)}
                        color="#374151"
                        icon={<BankOutlined />}
                        loading={summaryLoading}
                        sub="Шимтгэл гарц — техник"
                      />
                    </>
                  ) : (
                    <>
                      <StatCard
                        title="Банк орлого"
                        value={fmt(summary?.bank?.incoming)}
                        color="#2563eb"
                        icon={<BankOutlined />}
                        loading={summaryLoading}
                      />
                      <StatCard
                        title="Банк зарлага"
                        value={fmt(summary?.bank?.outgoing)}
                        color="#dc2626"
                        icon={<BankOutlined />}
                        loading={summaryLoading}
                      />
                      <StatCard
                        title="Дансанд үлдсэн"
                        value={fmt(summary?.bank?.netKept)}
                        color="#059669"
                        icon={<CheckCircleOutlined />}
                        loading={summaryLoading}
                      />
                      <StatCard
                        title="Систем гарц"
                        value={fmt(summary?.system?.systemOutgoing)}
                        color="#374151"
                        icon={<BankOutlined />}
                        loading={summaryLoading}
                      />
                    </>
                  )}
                  <StatCard
                    title="FAILED"
                    value={failedCount > 0 ? `${failedCount}` : "0"}
                    color={failedCount > 0 ? "#dc2626" : "#9ca3af"}
                    icon={<WarningOutlined />}
                    loading={summaryLoading}
                    sub={
                      failedCount > 0
                        ? fmt(summary?.reconciliation?.failedTransactionAmount)
                        : undefined
                    }
                  />
                </div>
                {isPosAccount && (
                  <ReconciliationDetail summary={summary} loading={summaryLoading} />
                )}
              </>
            ),
          },
        ]}
      />

      <Card styles={{ body: { padding: "8px 12px" } }} title="Дэлгэрэнгүй">
        <Tabs items={tabItems} size="small" />
      </Card>
    </div>
  );
};

export default Reconciliation;
